import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WizValidators } from '@commons/validators';
import { WindowResizeService } from '@core/services/window-resize/window-resize.service';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { ContactLocalMission } from '@models/event/contact';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LocalMissionWebservice } from '@webservices/local-mission/local-mission.webservice';
import { WINDOW } from '@wizbii/angular-utilities';
import { ContactResource, RecipientEmailEnum } from '@wizbii/models';
import { ContactWebservice } from '@wizbii/webservices';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

export enum ContactTypeEnum {
  normal = 'normal',
  localMission = 'local-mission',
  organizeEvent = 'organizeEvent',
  requestEventParticipation = 'requestEventParticipation',
}
interface ContactModalData {
  contactType: ContactTypeEnum;
  isMobile: boolean;
  promo1618?: boolean;
  localMission?: {
    email: string;
  };
}

@UntilDestroy()
@Component({
  templateUrl: './contact-modal.component.html',
  styleUrls: ['./contact-modal.component.scss'],
  animations: [
    trigger('loaderIndicator', [
      transition('* => void', [animate('.4s ease-in-out', style({ opacity: 0, transform: 'scaleX(1)' }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactModalComponent implements OnInit, OnDestroy {
  form: FormGroup;

  loading$ = new BehaviorSubject<boolean>(false);
  loadingLoader$ = new BehaviorSubject<boolean>(false);
  ContactTypeEnum = ContactTypeEnum;
  FeaturesRoutingEnum = FeaturesRoutingEnum;

  contactType: ContactTypeEnum;
  isMobile: boolean;

  ages = [...Array(15).keys()].map((item) => item + 16);

  private readonly templateKeys: Partial<Record<ContactTypeEnum, string>> = {
    [ContactTypeEnum.organizeEvent]: 'company_organize_event',
  };

  private _ieColumn = 1;

  get ieColumn(): number {
    if (this._ieColumn === 1) {
      this._ieColumn = 2;
      return 1;
    }

    this._ieColumn = 3;
    return 3;
  }

  private _ieRow = 2;
  get ieRow(): number {
    if (this._ieColumn === 3) {
      this._ieRow += 1;
      this._ieColumn = 1;
    }

    return this._ieRow;
  }

  get firstName(): FormControl {
    return this.form.get('firstName') as FormControl;
  }

  get lastName(): FormControl {
    return this.form.get('lastName') as FormControl;
  }

  get mail(): FormControl {
    return this.form.get('mail') as FormControl;
  }

  get phone(): FormControl {
    return this.form.get('phone') as FormControl;
  }

  get content(): FormControl {
    return this.form.get('content') as FormControl;
  }

  get company(): FormControl {
    return this.form.get('company') as FormControl;
  }

  get position(): FormControl {
    return this.form.get('position') as FormControl;
  }

  get age(): FormControl {
    return this.form.get('age') as FormControl;
  }

  get ratio$(): Observable<number> {
    return this.windowResizeService.ratio$;
  }

  get rgpdInfo(): string[] {
    return [
      'En cliquant sur "Etre recontacté", j\'accepte que mes données personnelles soient traitées pour que les organisateurs de l\'événement puissent me contacter.',
      'En cliquant sur "Être recontacté",  j\'accepte que mes données soient envoyées au partenaire Wizbii qui coordonne les événements sur le portail.',
    ];
  }

  constructor(
    @Inject(WINDOW) private readonly window: any,
    private readonly cdr: ChangeDetectorRef,
    readonly formBuilder: FormBuilder,
    readonly dialogRef: MatDialogRef<ContactModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ContactModalData,
    private readonly contactWebService: ContactWebservice,
    private readonly localMissionWebservice: LocalMissionWebservice,
    private readonly windowResizeService: WindowResizeService
  ) {
    this.contactType = data.contactType;
    this.isMobile = data.isMobile;

    this.form = this.formBuilder.group({
      firstName: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      lastName: [undefined, [Validators.required, WizValidators.noWhitespaceValidator]],
      mail: [undefined],
      phone: [undefined, [Validators.required, WizValidators.phone]],
      content: [undefined],
      company: [undefined],
      position: [undefined],
      age: [undefined],
    });

    switch (this.data.contactType) {
      case ContactTypeEnum.normal:
        this.content.setValidators([Validators.required, WizValidators.noWhitespaceValidator]);
        return;
      case ContactTypeEnum.localMission:
        this.age.setValidators(Validators.required);
        this.mail.setValidators([WizValidators.noWhitespaceValidator, WizValidators.email]);
        return;
      case ContactTypeEnum.organizeEvent:
        this.company.setValidators([Validators.required, WizValidators.noWhitespaceValidator]);
        this.content.setValidators([Validators.required, WizValidators.noWhitespaceValidator]);
        this.mail.setValidators([Validators.required, WizValidators.email]);
        return;
    }
  }
  ngOnInit(): void {
    if (this.isMobile) {
      this.dialogRef
        .backdropClick()
        .pipe(untilDestroyed(this))
        .subscribe({ next: () => this.closeDialog() });

      this.ratio$.pipe(untilDestroyed(this)).subscribe({
        next: (ratio) => {
          this.dialogRef.updateSize('100vw', `${100 * ratio - 50}px`);
          this.dialogRef.addPanelClass('wizbii-mobile-panel-open');
        },
      });
    }
  }

  closeDialog(data?: any): void {
    if (this.isMobile) {
      this.dialogRef.addPanelClass('wizbii-mobile-panel-close');
      this.window.setTimeout(() => {
        this.dialogRef.close(data);
      }, 100);
    } else {
      this.dialogRef.close(data);
    }
  }

  trackByAge = (_: number, age: number): number => age;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  closeDialogOnSend(): void {
    if (this.form.invalid) {
      return;
    }

    this.loading$.next(true);

    const contact: ContactResource = {
      firstName: this.firstName.value,
      lastName: this.lastName.value,
      phone: this.phone.value ? this.phone.value : '',
      content: this.content.value ? this.content.value : '',
      email: this.mail.value ? this.mail.value : '',
      appId: '1jeune1solution',
      subject: `À contacter - ${this.firstName.value} ${this.lastName.value}`,
      mustSendCopy: false,
      recipientEmailKey:
        environment.platform === 'prod' ? RecipientEmailEnum.UnJeuneUneSolution : RecipientEmailEnum.Moulteampass,
      attributes: {
        company: this.company.value ? this.company.value : null,
        position: this.position.value ? this.position.value : null,
        age: this.age.value ? this.age.value : null,
        templateKey: this.templateKeys[this.data.contactType] ?? 'user_contact_footer',
        promo1618: this.data.promo1618 ? 'Promo 16.18' : '',
      },
    };

    if (this.data.contactType === ContactTypeEnum.localMission) {
      const contactLocalMission: ContactLocalMission = {
        firstName: this.firstName.value,
        lastName: this.lastName.value,
        phoneNumber: this.phone.value,
        age: this.age.value,
        email: this.mail.value,
        localMissionEmail: this.data.localMission?.email ?? '',
      };

      this.localMissionWebservice
        .sendContactMail(contactLocalMission)
        .pipe(
          finalize(() => {
            this.endContact();
          })
        )
        .subscribe();
      return;
    }

    this.contactWebService
      .send(contact)
      .pipe(
        finalize(() => {
          this.endContact();
        })
      )
      .subscribe();
  }

  private endContact(): void {
    this.loadingLoader$.next(true);

    this.window.setTimeout(() => {
      this.loadingLoader$.next(false);
      this.cdr.detectChanges();

      this.window.setTimeout(() => {
        this.closeDialog(this.form.value);
        this.loading$.next(false);
        this.cdr.detectChanges();
      }, 1000);
    }, 1000);
  }

  ngOnDestroy(): void {
    this.loading$.complete();
    this.loadingLoader$.complete();
  }
}
